'use client';

import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { tenantLLMApi, LLMConfig, LLMConfigUpdate } from '@/services/tenant-llm-api';
import { CheckCircleIcon, XCircleIcon, KeyIcon } from '@heroicons/react/24/solid';
import Select, { SelectOption } from '@/components/common/Select';
import FormInput from '@/components/common/form/FormInput';
import { Button } from '@/components/common/Button';
import ConfirmDialog from '@/components/common/ConfirmDialog';

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}

const Loader2 = ({ className }: { className?: string }) => <span className={className}>⏳</span>;

export function LLMSettings() {
    const [config, setConfig] = useState<LLMConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [validating, setValidating] = useState(false);
    const [validationResult, setValidationResult] = useState<{ success: boolean; message?: string } | null>(null);

    const [formData, setFormData] = useState<LLMConfigUpdate>({
        provider: 'openai',
        apiKey: '',
        enabled: true,
        capabilities: {
            chat: true,
            image: false,
            video: false,
        },
    });

    const [isEditing, setIsEditing] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const [originalApiKey, setOriginalApiKey] = useState<string>(''); // Track original key
    const [originalProvider, setOriginalProvider] = useState<'openai' | 'gemini'>('openai');

    // Confirmation dialogs
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const data = await tenantLLMApi.getConfig();
            setConfig(data);

            if (data.isTenantOwned) {
                setFormData({
                    provider: data.provider,
                    apiKey: data.apiKey, // This will be masked from backend
                    enabled: data.enabled ?? true,
                    capabilities: data.capabilities || { chat: true, image: false, video: false },
                });
                setOriginalApiKey(data.apiKey);
                setOriginalProvider(data.provider);
            }
        } catch (error) {
            console.error('Failed to load LLM config:', error);
        } finally {
            setLoading(false);
        }
    };

    // Check if API key or provider changed (requires re-validation)
    const hasKeyOrProviderChanged = () => {
        if (!config?.isTenantOwned) return true; // New config, needs validation
        return formData.apiKey !== originalApiKey || formData.provider !== originalProvider;
    };

    const handleValidate = async () => {
        if (!formData.apiKey || formData.apiKey.includes('...')) {
            setValidationResult({ success: false, message: 'Please enter a valid API key' });
            return;
        }

        try {
            setValidating(true);
            setValidationResult(null);
            await tenantLLMApi.validateConfig(formData);
            setValidationResult({ success: true, message: 'API key is valid!' });
        } catch (error: any) {
            setValidationResult({ success: false, message: error.message || 'Validation failed' });
        } finally {
            setValidating(false);
        }
    };

    const handleSave = async () => {
        // Show billing warning when enabling BYOK (only for new configs)
        if (!config?.isTenantOwned) {
            setShowSaveConfirm(true);
            return;
        }

        // If already has config, save directly
        await performSave();
    };

    const performSave = async () => {
        try {
            setSaving(true);
            await tenantLLMApi.updateConfig(formData);
            await loadConfig();
            setIsEditing(false);
            setValidationResult(null);
        } catch (error) {
            console.error('Failed to save LLM config:', error);
            alert('Failed to save configuration. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setShowDeleteConfirm(true);
    };

    const performDelete = async () => {

        try {
            setSaving(true);
            await tenantLLMApi.deleteConfig();
            await loadConfig();
            setIsEditing(false);
            setFormData({ provider: 'openai', apiKey: '', enabled: true, capabilities: { chat: true, image: false, video: false } });
            setValidationResult(null);
        } catch (error) {
            console.error('Failed to delete LLM config:', error);
            alert('Failed to remove configuration. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    const isUsingSystemDefault = !config?.isTenantOwned;

    const providerOptions: SelectOption<'openai' | 'gemini'>[] = [
        { value: 'openai', label: 'OpenAI', description: 'GPT-4 and other OpenAI models' },
        { value: 'gemini', label: 'Google Gemini', description: 'Gemini Pro and Flash models' },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-gray-900">LLM Configuration</h2>
                <p className="mt-1 text-sm text-gray-600">
                    Bring your own OpenAI or Gemini API key to use your own quota
                </p>
            </div>

            {isUsingSystemDefault && !isEditing ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6">
                    <div className="text-center">
                        <KeyIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-semibold text-gray-900">Using System Default</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            You're currently using the platform's default LLM provider ({config?.provider}).
                        </p>
                        <Button
                            onClick={() => setIsEditing(true)}
                            className="mt-4"
                        >
                            Configure Custom Key
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
                    {/* Provider Selection */}
                    <div>
                        <label htmlFor="provider" className="block text-sm font-medium text-gray-700 mb-1">
                            Provider
                        </label>
                        <Select
                            id="provider"
                            value={formData.provider}
                            onChange={(value) => setFormData({ ...formData, provider: value })}
                            options={providerOptions}
                            disabled={!isEditing && config?.isTenantOwned}
                        />
                    </div>

                    {/* API Key Input */}
                    <div>
                        <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 mb-1">
                            API Key
                        </label>
                        <div className="flex rounded-md shadow-sm">
                            <FormInput
                                type={showApiKey ? 'text' : 'password'}
                                id="apiKey"
                                value={formData.apiKey}
                                onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                                disabled={!isEditing && config?.isTenantOwned}
                                placeholder={formData.provider === 'openai' ? 'sk-...' : 'Enter your Gemini API key'}
                                className="flex-1 rounded-r-none"
                            />
                            <button
                                type="button"
                                onClick={() => setShowApiKey(!showApiKey)}
                                className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm hover:bg-gray-100"
                            >
                                {showApiKey ? '🙈' : '👁️'}
                            </button>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                            {formData.provider === 'openai'
                                ? 'Get your API key from platform.openai.com'
                                : 'Get your API key from ai.google.dev'}
                        </p>
                    </div>

                    {/* Capabilities */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Capabilities
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                            Select which features should use your API key
                        </p>
                        <div className="space-y-3">
                            {/* Chat - Always enabled */}
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">Chat & Text Generation</p>
                                    <p className="text-xs text-gray-500">Required - Always uses your API key</p>
                                </div>
                                <Switch
                                    checked={true}
                                    onChange={() => { }}
                                    disabled={true}
                                    className="bg-blue-600 relative inline-flex h-6 w-11 flex-shrink-0 cursor-not-allowed rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out opacity-50"
                                >
                                    <span className="translate-x-5 pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out" />
                                </Switch>
                            </div>

                            {/* Image Generation */}
                            <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">Image Generation</p>
                                    <p className="text-xs text-gray-500">
                                        {formData.provider === 'openai' ? 'Requires DALL-E access' : 'Requires Imagen access'}
                                    </p>
                                </div>
                                <Switch
                                    checked={formData.capabilities?.image || false}
                                    onChange={(checked) => setFormData({
                                        ...formData,
                                        capabilities: { ...formData.capabilities!, image: checked }
                                    })}
                                    disabled={!isEditing && config?.isTenantOwned}
                                    className={classNames(
                                        formData.capabilities?.image ? 'bg-blue-600' : 'bg-gray-200',
                                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                                        (!isEditing && config?.isTenantOwned) ? 'opacity-50 cursor-not-allowed' : ''
                                    )}
                                >
                                    <span
                                        className={classNames(
                                            formData.capabilities?.image ? 'translate-x-5' : 'translate-x-0',
                                            'pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                        )}
                                    />
                                </Switch>
                            </div>

                            {/* Video Generation - Coming Soon */}
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md opacity-60">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">Video Generation</p>
                                    <p className="text-xs text-gray-500">Coming soon</p>
                                </div>
                                <Switch
                                    checked={false}
                                    onChange={() => { }}
                                    disabled={true}
                                    className="bg-gray-200 relative inline-flex h-6 w-11 flex-shrink-0 cursor-not-allowed rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out"
                                >
                                    <span className="translate-x-0 pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out" />
                                </Switch>
                            </div>
                        </div>
                    </div>

                    {/* Validation */}
                    {(isEditing || !config?.isTenantOwned) && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Button
                                    onClick={handleValidate}
                                    disabled={validating || !formData.apiKey || formData.apiKey.includes('...')}
                                    variant="outline"
                                    size="sm"
                                >
                                    {validating ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                            Validating...
                                        </>
                                    ) : (
                                        'Validate Key'
                                    )}
                                </Button>

                                {validationResult && (
                                    <div className={`flex items-center gap-1 text-sm ${validationResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                        {validationResult.success ? (
                                            <CheckCircleIcon className="h-5 w-5" />
                                        ) : (
                                            <XCircleIcon className="h-5 w-5" />
                                        )}
                                        <span>{validationResult.message}</span>
                                    </div>
                                )}
                            </div>

                            {hasKeyOrProviderChanged() && !validationResult?.success && (
                                <p className="text-xs text-amber-600">
                                    ⚠️ Please validate your API key before saving to ensure it works correctly.
                                </p>
                            )}

                            {!hasKeyOrProviderChanged() && (
                                <p className="text-xs text-blue-600">
                                    ℹ️ Only capabilities changed - validation not required.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div>
                            {config?.isTenantOwned && !isEditing && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                    Edit Configuration
                                </button>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {config?.isTenantOwned && (
                                <Button
                                    onClick={handleDelete}
                                    disabled={saving}
                                    variant="destructive"
                                    size="sm"
                                >
                                    Remove Custom Key
                                </Button>
                            )}
                            {(isEditing || !config?.isTenantOwned) && (
                                <>
                                    {config?.isTenantOwned && (
                                        <Button
                                            onClick={() => {
                                                setIsEditing(false);
                                                loadConfig();
                                                setValidationResult(null);
                                            }}
                                            disabled={saving}
                                            variant="outline"
                                            size="sm"
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    <Button
                                        onClick={handleSave}
                                        disabled={
                                            saving ||
                                            !formData.apiKey ||
                                            (hasKeyOrProviderChanged() && !validationResult?.success)
                                        }
                                        size="sm"
                                        title={
                                            hasKeyOrProviderChanged() && !validationResult?.success
                                                ? 'Please validate your API key first'
                                                : ''
                                        }
                                    >
                                        {saving ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Info Box */}
            <div className="rounded-md bg-blue-50 p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3 flex-1">
                        <p className="text-sm text-blue-700">
                            When you configure your own API key, AI operations (image generation, content generation) will use your quota instead of the platform's quota. This gives you more control and potentially higher limits.
                        </p>
                    </div>
                </div>
            </div>

            {/* Save Confirmation Dialog */}
            <ConfirmDialog
                isOpen={showSaveConfirm}
                onClose={() => setShowSaveConfirm(false)}
                onConfirm={async () => {
                    setShowSaveConfirm(false);
                    await performSave();
                }}
                title="Enable BYOK"
                message={`Enabling BYOK will update your billing:\n\n• Starter Plan: Immediately switch to $9.90/month (50% discount)\n• Professional+: No price change (privacy benefit only)\n\nYou will be charged a prorated amount for the remainder of this billing cycle.`}
                confirmText="Enable BYOK"
                cancelText="Cancel"
                confirmButtonClassName="bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500"
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={async () => {
                    setShowDeleteConfirm(false);
                    await performDelete();
                }}
                title="Remove Custom API Key"
                message={`Removing your custom API key will:\n\n• Revert to using platform AI services\n• Starter Plan: Return to $19.90/month at next billing cycle\n• Professional+: No price change\n\nAre you sure?`}
                confirmText="Remove Key"
                cancelText="Cancel"
            />
        </div>
    );
}

export default LLMSettings;
